import {
  AddClassPostCommentMutationVariables,
  LearnerSinglePostQuery,
  LearnerSinglePostQueryVariables,
} from "@outschool/gql-frontend-generated";
import {
  ApolloCache,
  gql,
  Reference,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import {
  ClassPostCommentFragment,
  ClassPostFragments,
  ClassPostMessageContentFragment,
  ClassPostSenderFragment,
} from "@outschool/ui-components-classroom";

import { UpdateClassroomCache } from "../hooks/useLearnerClassroomAddClassPostCommentMutation";

const LearnerClassPostWithCommentsFragment = gql`
  fragment LearnerClassPost_classPostWithComments on ClassPost {
    publishAt
    isWelcomePost
    sender {
      ...ClassPost_sender
    }
    messageContent {
      ...ClassPost_messageContent
    }
    comments(filters: { creationDateSortOrder: $creationDateSortOrder }) {
      ...ClassPost_comment
    }
  }
  ${ClassPostSenderFragment}
  ${ClassPostMessageContentFragment}
  ${ClassPostCommentFragment}
`;

export const singlePostQuery = gql`
  query LearnerSinglePost(
    $sectionUid: ID!
    $classPostUid: ID!
    $plainText: Boolean
    $creationDateSortOrder: SortOrder
  ) {
    classroom(sectionUid: $sectionUid) {
      uid
      usersCanComment
      postByUid(classPostUid: $classPostUid) {
        uid
        title
        estimatedDuration
        blockType
        learnerState {
          videoProgress
          videoWatchCount
          completedAt
          heartedAt
          lastViewedAt
        }
        ...LearnerClassPost_classPostWithComments
      }
      mentionNames {
        ...ClassPost_mentionNames
      }
    }
  }
  ${LearnerClassPostWithCommentsFragment}
  ${ClassPostFragments.mentionNames}
`;

export const useSinglePostQuery = ({
  sectionUid,
  classPostUid,
  skip = false,
  creationDateSortOrder,
}: {
  sectionUid: string;
  classPostUid: string;
  skip?: boolean;
  // @ts-ignore TS7
  creationDateSortOrder?: LearnerSinglePostQueryVariables["creationDateSortOrder"];
}) =>
  useQueryWithPreviousData<
    LearnerSinglePostQuery,
    LearnerSinglePostQueryVariables
  >(singlePostQuery, {
    variables: {
      sectionUid,
      classPostUid,
      plainText: false,
      // @ts-ignore TS7
      creationDateSortOrder,
    },
    fetchPolicy: "cache-and-network",
    skip: skip || !classPostUid,
  });

export const updateLearnerClassroomSinglePostsCache: UpdateClassroomCache = (
  cache: ApolloCache<any>,
  variables: AddClassPostCommentMutationVariables,
  newClassPostComment,
) => {
  cache.modify({
    // If modifying, reference shape of LearnerSinglePostQuery generated type for details
    id: cache.identify({
      __typename: "ClassPost",
      uid: variables.classPostCommentInput.classPostUid,
    }),
    fields: {
      // @ts-ignore TS7
      comments(prevCommentsRef: Reference[], { storeFieldName, toReference }) {
        if (
          prevCommentsRef &&
          // Sanity check comments being an array
          Array.isArray(prevCommentsRef) &&
          newClassPostComment?.addClassPostComment
        ) {
          const newCommentRef = toReference(
            newClassPostComment.addClassPostComment,
          );
          if (!newCommentRef) {
            return prevCommentsRef;
          }
          return storeFieldName.includes('"creationDateSortOrder":"descending"')
            ? [newCommentRef, ...prevCommentsRef]
            : [...prevCommentsRef, newCommentRef];
        }
        return prevCommentsRef;
      },
    },
  });
};
